import React from 'react';
import { Grid, styled, Typography } from '@mui/material';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { FieldError, UseFormReturn, useFormState } from 'react-hook-form';
import get from 'lodash/get';
import * as lookupSlice from '../../../data/lookup.slice';
import * as accountingSlice from '../../../data/accounting.slice';
import { useAppSelector } from '../../../hooks/useAppSelector';
import {
  ChargeType,
  ChargeTypeInstance,
  PricingFunction,
} from '../../../data/models/pricing-model.model';
import TextInput from '../../forms/TextInput';
import SelectMenu from '../../forms/SelectMenu';
import CurrencyInputField from '../../forms/CurrencyInputField';

const PREFIX = 'ChargeTypeItem';

export const classes = {
  selectContainer: `${PREFIX}-select-container`,
  type: `${PREFIX}-type`,
  title: `${PREFIX}-title`,
  subtitle: `${PREFIX}-subtitle`,
};

export const StyledGrid = styled(Grid)(({ theme }) => ({
  [`&.${classes.selectContainer} > .MuiFormControl-root`]: {
    display: 'flex',
  },
  [`& .${classes.type}`]: {
    fontFeatureSettings: "'liga' off, 'clig' off",
    fontFamily: 'Source Sans Pro',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '143%',
    letterSpacing: '0.17px',
    paddingBottom: 0,
  },
  [`& .${classes.title}`]: {
    color: theme.palette.lightTextPrimary.main,
  },
  [`& .${classes.subtitle}`]: {
    color: theme.palette.lightActionDisabled60p.main,
  },
}));

export default function ChargeTypeItem({
  chargeType,
  formHook,
  idx,
  displayInputLabels,
  allParamsRequired,
}: {
  chargeType: ChargeTypeInstance;
  formHook: UseFormReturn<any, any>;
  idx: number;
  displayInputLabels?: boolean;
  allParamsRequired?: boolean;
}) {
  const t = useCommonTranslation();
  const requiredFieldMessage = t('common.requiredField');
  const { errors } = useFormState({ control: formHook.control });

  const chargeTypes = useAppSelector(lookupSlice.selectChargeTypes);
  const pricingFunctions = useAppSelector(
    accountingSlice.selectPricingFunctions,
  );

  const getChargeType = (chargeTypeBC: string): ChargeType | undefined => {
    return chargeTypes.find((c) => c.chargeTypeBC === chargeTypeBC);
  };

  const getPricingFunction = (
    pricingFunctionBC: string,
  ): PricingFunction | undefined => {
    return pricingFunctions.find(
      (c) => c.pricingFunctionBC === pricingFunctionBC,
    );
  };

  const ct = getChargeType(chargeType.chargeTypeBC);
  const pricingFunction = getPricingFunction(chargeType.pricingFunctionBC);
  const params =
    pricingFunction?.parameters.filter((p) => p.type === 'UserSupplied') || [];
  const isPassthrough =
    pricingFunction?.pricingFunctionBC === 'ClientPassthrough' ||
    pricingFunction?.pricingFunctionBC === 'ProviderPassthrough';

  const roudingOptions = [
    { label: t('client.pricingWhole'), value: 'whole' },
    { label: t('client.pricingHalf'), value: 'half' },
    { label: t('client.pricingTenth'), value: 'tenth' },
  ];

  const getPricingFunctionName = (bc?: string) => {
    return getPricingFunction(bc || '')?.name || '';
  };

  return (
    <React.Fragment key={`${idx}`}>
      <StyledGrid item xs={3}>
        <Typography className={`${classes.type} ${classes.title}`}>
          {ct?.chargeTypeName}
        </Typography>
        <Typography className={`${classes.type} ${classes.subtitle}`}>
          {getPricingFunctionName(pricingFunction?.pricingFunctionBC)}
        </Typography>
      </StyledGrid>
      {isPassthrough && (
        <Grid item xs={6}>
          {pricingFunction.name}
        </Grid>
      )}
      {!isPassthrough && (
        <>
          {params.map((p) => {
            const isRoundTo = p.name === 'roundTo';
            const isRoundUpIncrement = p.name === 'roundUpIncrement';
            const isFieldRequired = allParamsRequired || chargeType.isRequired;

            const fieldName = `Params[${idx}].${p.name}`;
            const fieldError = get(errors, fieldName) as FieldError | undefined;

            const commonProps = {
              name: fieldName,
              control: formHook.control,
              required: isFieldRequired,
              label: displayInputLabels ? p.name : '',
              rules: {
                ...(isFieldRequired && { required: requiredFieldMessage }),
                ...(isRoundUpIncrement && {
                  min: { value: 1, message: t('common.positiveNumberField') },
                }),
              },
            };

            return isRoundTo ? (
              <StyledGrid
                className={classes.selectContainer}
                item
                xs={3}
                key={`${idx}_${p.name}`}>
                <SelectMenu
                  {...commonProps}
                  values={roudingOptions}
                  valueAttr="value"
                  labelAttr="label"
                  inputProps={{
                    sx: {
                      '&.MuiInputBase-input': {
                        color: 'lightTextSecondary.main',
                      },
                    },
                  }}
                />
              </StyledGrid>
            ) : (
              <Grid item xs={3} key={`${idx}_${p.name}`}>
                <CurrencyInputField
                  {...commonProps}
                  currencyInputProps={{
                    placeholder: displayInputLabels ? p.name : '',
                    step: 1,
                    ...(isRoundUpIncrement && {
                      allowDecimals: false,
                      allowNegativeValue: false,
                      decimalScale: 0,
                    }),
                  }}
                  textFieldProps={{
                    error: !!fieldError,
                    helperText: fieldError?.message,
                    sx: {
                      '& .MuiInputBase-input': {
                        textAlign: 'right',
                        color: 'lightTextSecondary.main',
                        '&::placeholder': {
                          color: 'lightActionDisabled60p.main',
                          opacity: 1,
                        },
                      },
                    },
                  }}
                />
              </Grid>
            );
          })}
        </>
      )}

      {params.length === 0 && !isPassthrough && <Grid item xs={6} />}
      {params.length === 1 && <Grid item xs={3} />}
      <Grid item xs={3}>
        <TextInput
          name={`Params[${idx}].cptCodeBC`}
          label={displayInputLabels ? t('client.cptCode') : ''}
          control={formHook.control}
          inputProps={{
            sx: {
              '&.MuiInputBase-input': {
                color: 'lightTextSecondary.main',
              },
            },
          }}
        />
      </Grid>
    </React.Fragment>
  );
}
